// Login-Gate (F-konten-05, F-konten-11/12): Ohne Sitzung → Login mit ?next=, ohne Plattform-Recht →
// Keine-Rechte-Zustand. Die Entscheidung ist eine reine Funktion (testbar), RequireAuth rendert sie.
import type { Me } from '@bar/contracts/core';
import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { PlatformKey } from '@web/registry/types';
import { type AuthStatus, useAuth } from './context';

export type GateDecision =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'login'; to: string }
  | { kind: 'forbidden' }
  | { kind: 'allow' };

export interface GateInput {
  status: AuthStatus;
  me: Me | null;
  platform: PlatformKey;
  /** Aktueller Pfad inkl. Query (relativ zur Plattform-Basis). */
  path: string;
  /** Login-Seite der Plattform (People/Admin: '/login'). */
  loginPath?: string;
}

export function gateDecision({
  status,
  me,
  platform,
  path,
  loginPath = '/login',
}: GateInput): GateDecision {
  if (status === 'loading') return { kind: 'loading' };
  if (status === 'error') return { kind: 'error' };
  if (status === 'anonymous' || !me) {
    const next = safeNextPath(path);
    return {
      kind: 'login',
      to: next && next !== '/' ? `${loginPath}?next=${encodeURIComponent(next)}` : loginPath,
    };
  }
  if (!me.platforms[platform]) return { kind: 'forbidden' };
  return { kind: 'allow' };
}

/**
 * Ziel für `?next=` nur innerhalb der App: absoluter Pfad, kein `//host`, kein Backslash, keine
 * Steuerzeichen. Sonst undefined (Open-Redirect-Schutz).
 */
export function safeNextPath(next: string | null | undefined): string | undefined {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.includes('\\'))
    return undefined;
  if ([...next].some((ch) => ch.charCodeAt(0) < 0x20)) return undefined;
  try {
    const url = new URL(next, 'http://bar.invalid');
    return url.origin === 'http://bar.invalid'
      ? `${url.pathname}${url.search}${url.hash}`
      : undefined;
  } catch {
    return undefined;
  }
}

/** Volle Navigation (andere Plattform) als Effekt, nicht während des Renderns. */
function ExternalRedirect({ to, children }: { to: string; children: ReactNode }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <>{children}</>;
}

export interface RequireAuthProps {
  platform: PlatformKey;
  children: ReactNode;
  loginPath?: string;
  /** Skeleton der Shell während /me lädt. */
  fallback?: ReactNode;
  /** Keine-Rechte-Bildschirm (Admin: /admin/forbidden-Inhalt). */
  forbidden?: ReactNode;
  /** Fehlerzustand mit Retry. */
  error?: ReactNode;
  /**
   * Plattform-übergreifendes Login (KI → People-Login): volle Navigation statt Router; `basePath`
   * (z. B. '/ai') wird dem Rücksprungziel vorangestellt.
   */
  externalLogin?: boolean;
  basePath?: string;
}

export function RequireAuth(props: RequireAuthProps) {
  const { status, me } = useAuth();
  const location = useLocation();
  const decision = gateDecision({
    status,
    me,
    platform: props.platform,
    path: `${props.basePath ?? ''}${location.pathname}${location.search}`,
    loginPath: props.loginPath,
  });
  switch (decision.kind) {
    case 'loading':
      return <>{props.fallback ?? null}</>;
    case 'error':
      return <>{props.error ?? props.fallback ?? null}</>;
    case 'forbidden':
      return <>{props.forbidden ?? null}</>;
    case 'login':
      return props.externalLogin ? (
        <ExternalRedirect to={decision.to}>{props.fallback ?? null}</ExternalRedirect>
      ) : (
        <Navigate to={decision.to} replace />
      );
    case 'allow':
      return <>{props.children}</>;
  }
}
